import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import sha1 from 'crypto-js/sha1';
import LogRocket from 'logrocket';
import axios from '@api';
import {
  getIntercomAppId,
  hasIntercomFailed,
  loadIntercom,
  markIntercomFailed,
} from './utilities/helpers';

import { MAX_RETRIES, RETRY_DELAY } from './utilities/constants';

const HAS_INTERCOM_APP_ID = Boolean(getIntercomAppId());

const Intercom = () => {
  const { data, isDataLoaded } = useSelector((state) => state.user);
  const { clientInfo, email, firstName, lastName } = data || {};
  const [userHash, setUserHash] = useState(null);
  const [backendUserId, setBackendUserId] = useState(null);
  const [isLoadingHash, setIsLoadingHash] = useState(false);
  const [hasAttempted, setHasAttempted] = useState(false);

  const isMountedRef = useRef(true);
  const retryTimeoutRef = useRef(null);
  const hasBootedRef = useRef(false);
  const clientInfoRef = useRef(clientInfo);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  // Fetch secure user hash from backend with retry logic
  const fetchUserHash = async (attempt = 1) => {
    try {
      if (!isMountedRef.current) return;

      setIsLoadingHash(true);

      const response = await axios({
        method: 'get',
        url: '/api/intercom/user-hash',
        withCredentials: true,
      });

      const payload = response.data || {};
      const userHashValue = payload.user_hash || payload.userHash;
      const userIdValue = payload.user_id || payload.userId;

      if (!userHashValue || !userIdValue) {
        throw new Error('Invalid API response format');
      }

      setUserHash(userHashValue);
      setBackendUserId(userIdValue);
      setIsLoadingHash(false);
    } catch (error) {
      if (!isMountedRef.current) return;

      if (attempt < MAX_RETRIES) {
        retryTimeoutRef.current = setTimeout(
          () => fetchUserHash(attempt + 1),
          RETRY_DELAY * attempt // simple backoff
        );
      } else {
        console.error('Intercom widget failed to initialize due to authentication issues.', error);
        setIsLoadingHash(false);
        setHasAttempted(true);
        markIntercomFailed('intercom-user-hash-failed', error);
      }
    }
  };

  // reload user hash when clientInfo changes
  useEffect(() => {
    if (!clientInfo) return;
    const prevClientInfo = clientInfoRef.current;
    if (prevClientInfo && prevClientInfo.id !== clientInfo.id) {
      setUserHash(null);
      setBackendUserId(null);
      setHasAttempted(false);
    }
    clientInfoRef.current = clientInfo;
  }, [clientInfo]);

  // initial load if not yet attempted
  useEffect(() => {
    if (
      HAS_INTERCOM_APP_ID &&
      !hasIntercomFailed() &&
      isDataLoaded &&
      !userHash &&
      !isLoadingHash &&
      !hasAttempted
    ) {
      setHasAttempted(true);
      fetchUserHash();
    }
  }, [isDataLoaded, userHash, isLoadingHash, hasAttempted]);

  useEffect(() => {
    if (!HAS_INTERCOM_APP_ID || hasIntercomFailed()) {
      return undefined;
    }

    if (typeof window === 'undefined') {
      return undefined;
    }

    if (userHash === null || backendUserId === null) {
      return undefined;
    }

    if (!window.Intercom) {
      try {
        loadIntercom();
      } catch (error) {
        markIntercomFailed('intercom-load-call-failed', error);
        return undefined;
      }
    }

    // Only initialize/update Intercom when we have user data AND successfully fetched the user hash
    if (isDataLoaded && userHash && backendUserId && !hasIntercomFailed()) {
      const companyName = clientInfo?.name || 'Unknown Company';
      if (!clientInfo?.name) {
        console.error('[Intercom] Client name unresolved; using fallback "Unknown Company".', {
          clientInfoSnapshot: clientInfo,
          email,
          userId: backendUserId,
        });
      }

      const intercomConfig = {
        app_id: getIntercomAppId(),
        company: {
          id: sha1(companyName).toString(),
          name: companyName,
        },
        email,
        name: `${firstName || ''} ${lastName || ''}`.trim(),
        user_hash: userHash, // REQUIRED: Identity verification hash
        user_id: backendUserId.toString(), // CRITICAL: Use the SAME user_id that was used to generate the hash
      };

      try {
        if (!hasBootedRef.current) {
          window.Intercom('boot', intercomConfig);
          hasBootedRef.current = true;
        } else {
          window.Intercom('update', intercomConfig);
        }

        LogRocket.getSessionURL((sessionURL) => {
          try {
            window.Intercom('trackEvent', 'LogRocket', { sessionURL });
          } catch (error) {
            markIntercomFailed('intercom-track-event-failed', error);
          }
        });
      } catch (error) {
        markIntercomFailed('intercom-boot-failed', error);
      }
    }

    return () => {
      if (typeof window === 'undefined') return;
      if (hasBootedRef.current && window.Intercom) {
        try {
          window.Intercom('shutdown');
        } catch (error) {
          console.error('[Intercom] shutdown failed (non-blocking)', error);
        } finally {
          hasBootedRef.current = false;
        }
      }
    };
  }, [isDataLoaded, userHash, backendUserId]);

  return null;
};

export default Intercom;
